import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Popover,
  IconButton,
  Divider,
  TextField,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  PendingActions,
  EventAvailable,
  Group,
  EventBusy,
  CalendarMonth,
  Business,
  NavigateBefore,
  NavigateNext,
  TrendingUp,
  Person,
  ThumbUp,
  ThumbDown,
  Visibility,
  Assessment,
  BarChart,
  Close,
  Print as PrintIcon,
  People,
  Analytics,
} from "@mui/icons-material";
import { leaveService, PendingApproval } from "../../services/leaveService";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";

interface DepartmentStats {
  department_name: string;
  on_leave_today: number;
  total_staff: number;
  pending_approvals: number;
}

interface StaffOnLeave {
  leave_id: number;
  name: string;
  department_name: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  number_of_days: number;
  status: string;
}

const VCDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Statistics
  const [vcStats, setVcStats] = useState({
    totalStaff: 0,
    onLeaveToday: 0,
    pendingApprovals: 0,
    approvedThisMonth: 0,
    totalDepartments: 0,
  });

  const [departmentStats, setDepartmentStats] = useState<DepartmentStats[]>([]);
  const [staffOnLeaveToday, setStaffOnLeaveToday] = useState<StaffOnLeave[]>(
    [],
  );
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>(
    [],
  );

  // Calendar state
  const [teamCalendarOpen, setTeamCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarData, setCalendarData] = useState<any>({});

  // Approval dialog state
  const [selectedApplication, setSelectedApplication] =
    useState<PendingApproval | null>(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalHistory, setApprovalHistory] = useState<any[]>([]);
  const [comments, setComments] = useState("");

  // Ref for scrolling to pending approvals
  const pendingApprovalsRef = React.useRef<HTMLDivElement>(null);

  // Detailed Analytics state
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsMonth, setAnalyticsMonth] = useState(
    new Date().getMonth() + 1,
  );
  const [analyticsYear, setAnalyticsYear] = useState(new Date().getFullYear());

  // Calendar popover
  const [calendarPopover, setCalendarPopover] = useState<{
    anchorEl: HTMLElement | null;
    date: string;
    leaves: any[];
  }>({ anchorEl: null, date: "", leaves: [] });

  useEffect(() => {
    loadVCData();

    const onFocus = () => loadVCData();
    window.addEventListener("focus", onFocus);
    const interval = setInterval(loadVCData, 60000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(interval);
    };
  }, []);

  const loadVCData = async () => {
    try {
      setLoading(true);

      // Load pending approvals
      const approvals = await leaveService.getPendingApprovals();
      setPendingApprovals(approvals);

      // Load VC statistics
      const statsResponse = await api.get("/vc/statistics");
      setVcStats(statsResponse.data);

      // Load department-wise statistics
      const deptStatsResponse = await api.get("/vc/department-statistics");
      setDepartmentStats(deptStatsResponse.data);

      // Load staff on leave today
      const staffOnLeaveResponse = await api.get("/vc/staff-on-leave-today");
      setStaffOnLeaveToday(staffOnLeaveResponse.data);

      setError("");
    } catch (error: any) {
      console.error("Failed to load VC data:", error);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const loadCalendarData = async (month: number, year: number) => {
    try {
      setLoading(true);
      const response = await api.get("/vc/calendar", {
        params: {
          month: month + 1,
          year: year,
        },
      });

      const calData = response.data.calendar || {};
      setCalendarData(calData);
    } catch (error: any) {
      console.error("Failed to load calendar data:", error);
      setError("Failed to load calendar data");
      setCalendarData({});
    } finally {
      setLoading(false);
    }
  };

  const handleViewPendingApprovals = () => {
    pendingApprovalsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleAct = async (application: PendingApproval) => {
    // Re-check current state before opening the dialog to avoid acting on stale data
    try {
      const response = await api.get(`/leaves/${application.id}/details`);
      const leave = response.data.leave;
      const stillActionable =
        leave &&
        ["pending", "cancellation_pending"].includes(leave.status) &&
        Number(leave.current_approver_id) === Number(user?.id);

      if (!stillActionable) {
        setPendingApprovals((prev) =>
          prev.filter((app) => app.id !== application.id),
        );
        setError(
          "This application was already actioned or is no longer assigned to you. The list has been refreshed.",
        );
        await loadVCData();
        return;
      }

      setSelectedApplication(application);
      setApprovalDialogOpen(true);
      setApprovalHistory(response.data.approvalHistory || []);
    } catch (err) {
      console.error("Failed to load approval history:", err);
      setSelectedApplication(application);
      setApprovalDialogOpen(true);
      setApprovalHistory([]);
    }
  };

  const processApproval = async (action: string) => {
    if (!selectedApplication) return;
    try {
      setLoading(true);
      await api.post("/approvals/process", {
        applicationId: selectedApplication.id,
        action,
        comments,
      });
      setPendingApprovals((prev) =>
        prev.filter((app) => app.id !== selectedApplication.id),
      );
      setVcStats((prev) => ({
        ...prev,
        pendingApprovals: Math.max(0, prev.pendingApprovals - 1),
      }));
      setApprovalDialogOpen(false);
      setSelectedApplication(null);
      setComments("");
      setApprovalHistory([]);
      setError("");
      await loadVCData();
    } catch (error: any) {
      console.error("Failed to process approval:", error);
      setError("Failed to process approval. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Load detailed analytics
  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const response = await api.get(
        `/vc/analytics?year=${analyticsYear}&month=${analyticsMonth}`,
      );
      setAnalyticsData(response.data);
    } catch (err) {
      console.error("Failed to load analytics:", err);
      setError("Failed to load analytics data");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleViewTeamCalendar = async () => {
    setTeamCalendarOpen(true);
    await loadCalendarData(calendarMonth, calendarYear);
  };

  const handlePrevMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  };

  useEffect(() => {
    if (teamCalendarOpen) {
      loadCalendarData(calendarMonth, calendarYear);
    }
  }, [calendarMonth, calendarYear, teamCalendarOpen]);

  const renderCalendar = () => {
    const firstDay = new Date(calendarYear, calendarMonth, 1);
    const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <Box
          key={`empty-${i}`}
          sx={{ p: 1, minHeight: 80, bgcolor: "#fafafa" }}
        />,
      );
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const dayData = calendarData[dateStr] || {
        approved: 0,
        pending: 0,
        employees: [],
      };
      const currentDate = new Date(calendarYear, calendarMonth, day);
      const isToday = currentDate.getTime() === today.getTime();
      const isWeekend =
        currentDate.getDay() === 0 || currentDate.getDay() === 6;

      const hasContent = dayData.approved > 0 || dayData.pending > 0;

      days.push(
        <Box
          key={day}
          sx={{
            p: 1,
            minHeight: 80,
            border: "1px solid #e0e0e0",
            bgcolor: isToday ? "#e3f2fd" : isWeekend ? "#f5f5f5" : "white",
            cursor: hasContent ? "pointer" : "default",
            "&:hover": {
              bgcolor: hasContent ? "#f0f0f0" : "inherit",
            },
          }}
          onClick={(e) => {
            if (hasContent) {
              setCalendarPopover({
                anchorEl: e.currentTarget,
                date: dateStr,
                leaves: dayData.employees || [],
              });
            }
          }}
        >
          <Typography
            variant="caption"
            fontWeight={isToday ? "bold" : "normal"}
          >
            {day}
          </Typography>
          {dayData.approved > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={`${dayData.approved} approved`}
                size="small"
                sx={{ fontSize: "0.65rem", height: 20, mb: 0.5 }}
                color="success"
              />
            </Box>
          )}
          {dayData.pending > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={`${dayData.pending} pending`}
                size="small"
                sx={{ fontSize: "0.65rem", height: 20 }}
                color="warning"
              />
            </Box>
          )}
        </Box>,
      );
    }

    return days;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "error";
      case "cancellation_pending":
        return "info";
      case "cancelled":
        return "default";
      default:
        return "default";
    }
  };

  if (loading && vcStats.totalStaff === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* Quick Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<PendingActions />}
              onClick={handleViewPendingApprovals}
              disabled={pendingApprovals.length === 0}
            >
              View Pending Approvals ({pendingApprovals.length})
            </Button>
            <Button
              variant="outlined"
              startIcon={<CalendarMonth />}
              onClick={handleViewTeamCalendar}
            >
              View University Calendar
            </Button>
            <Button
              variant="outlined"
              startIcon={<Assessment />}
              onClick={() => {
                setAnalyticsOpen(true);
                loadAnalytics();
              }}
            >
              View Detailed Analytics
            </Button>
            <Button
              variant="outlined"
              startIcon={<TrendingUp />}
              onClick={loadVCData}
              disabled={loading}
            >
              Refresh Data
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Group color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {vcStats.totalStaff}
                  </Typography>
                  <Typography color="textSecondary">Total Staff</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: "#fff3e0" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <EventBusy color="warning" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {vcStats.onLeaveToday}
                  </Typography>
                  <Typography color="textSecondary">On Leave Today</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: vcStats.pendingApprovals > 0 ? "pointer" : "default",
              transition: "box-shadow 0.2s, transform 0.2s",
              "&:hover":
                vcStats.pendingApprovals > 0
                  ? {
                      boxShadow: 4,
                      transform: "translateY(-2px)",
                    }
                  : {},
            }}
            onClick={() =>
              vcStats.pendingApprovals > 0 && handleViewPendingApprovals()
            }
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PendingActions color="info" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography
                    variant="h4"
                    component="div"
                    color={
                      vcStats.pendingApprovals > 0 ? "info.main" : "inherit"
                    }
                  >
                    {vcStats.pendingApprovals}
                  </Typography>
                  <Typography color="textSecondary">
                    Pending Approvals
                  </Typography>
                  {vcStats.pendingApprovals > 0 && (
                    <Typography variant="caption" color="info.main">
                      Click to view
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <EventAvailable color="success" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {vcStats.approvedThisMonth}
                  </Typography>
                  <Typography color="textSecondary">
                    Approved This Month
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pending Approvals Table */}
      <div ref={pendingApprovalsRef}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <PendingActions sx={{ mr: 1 }} />
              Pending Approvals ({pendingApprovals.length})
            </Typography>

            {pendingApprovals.length === 0 ? (
              <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
                No pending approvals awaiting your action.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Leave Type</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Days</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingApprovals.map((application) => (
                      <TableRow key={application.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {application.employee_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {application.employee_code}
                            </Typography>
                            {application.status === "cancellation_pending" && (
                              <Chip
                                label="CANCELLATION"
                                size="small"
                                color="info"
                                sx={{ ml: 1, fontSize: "0.65rem", height: 20 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{application.department_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={application.leave_type}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(
                              application.start_date,
                            ).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            to{" "}
                            {new Date(
                              application.end_date,
                            ).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {application.number_of_days} day
                            {application.number_of_days !== 1 ? "s" : ""}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={application.reason}
                          >
                            {application.reason}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleAct(application)}
                              disabled={loading}
                              sx={{ minWidth: 70 }}
                            >
                              Act
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() =>
                                navigate(`/leaves/${application.id}`)
                              }
                              sx={{ minWidth: 70 }}
                            >
                              View
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Staff on Leave Today */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Person sx={{ mr: 1 }} />
            Staff on Leave Today
          </Typography>

          {staffOnLeaveToday.length === 0 ? (
            <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
              No staff members on leave today
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Leave Type</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Days</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staffOnLeaveToday.map((staff, index) => (
                    <TableRow
                      key={index}
                      hover
                      onClick={() => navigate(`/leaves/${staff.leave_id}`)}
                      sx={{ cursor: staff.leave_id ? "pointer" : "default" }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {staff.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{staff.department_name}</TableCell>
                      <TableCell>{staff.leave_type}</TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(staff.start_date).toLocaleDateString()} -{" "}
                          {new Date(staff.end_date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>{staff.number_of_days}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            staff.status.charAt(0).toUpperCase() +
                            staff.status.slice(1)
                          }
                          size="small"
                          color={getStatusColor(staff.status)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Department-wise Statistics */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Business sx={{ mr: 1 }} />
            Department-wise Summary
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Department</TableCell>
                  <TableCell align="right">Total Staff</TableCell>
                  <TableCell align="right">On Leave Today</TableCell>
                  <TableCell align="right">Pending Approvals</TableCell>
                  <TableCell align="right">Attendance %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {departmentStats.map((dept, index) => {
                  const attendanceRate =
                    dept.total_staff > 0
                      ? (
                          ((dept.total_staff - dept.on_leave_today) /
                            dept.total_staff) *
                          100
                        ).toFixed(1)
                      : "0.0";

                  return (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {dept.department_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{dept.total_staff}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={dept.on_leave_today}
                          size="small"
                          color={
                            dept.on_leave_today > 0 ? "warning" : "default"
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        {dept.pending_approvals}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={
                            parseFloat(attendanceRate) >= 90
                              ? "success.main"
                              : "text.primary"
                          }
                          fontWeight="medium"
                        >
                          {attendanceRate}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Detailed Analytics Dialog */}
      <Dialog
        open={analyticsOpen}
        onClose={() => setAnalyticsOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Assessment sx={{ mr: 1 }} color="primary" />
            Detailed Leave Analytics
          </Box>
          <IconButton onClick={() => setAnalyticsOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {/* Analytics Controls */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 3,
              mt: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={analyticsMonth}
                label="Month"
                onChange={(e) => setAnalyticsMonth(e.target.value as number)}
              >
                {[
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ].map((m, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={analyticsYear}
                label="Year"
                onChange={(e) => setAnalyticsYear(e.target.value as number)}
              >
                {Array.from(
                  { length: 5 },
                  (_, i) => new Date().getFullYear() - i,
                ).map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              onClick={loadAnalytics}
              disabled={analyticsLoading}
            >
              {analyticsLoading ? (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              ) : null}
              Generate
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={() => {
                const el = document.getElementById("vc-analytics-print-area");
                if (!el) return;
                const printWindow = window.open("", "_blank");
                if (!printWindow) {
                  alert("Please allow popups");
                  return;
                }
                printWindow.document.write(`
                  <!DOCTYPE html><html><head>
                  <title>Leave Analytics - ${analyticsMonth}/${analyticsYear}</title>
                  <style>
                    @media print { @page { size: landscape; margin: 10mm; } }
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    h1 { font-size: 18px; text-align: center; color: #1976d2; }
                    h2 { font-size: 14px; margin-top: 25px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
                    table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 20px; }
                    th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
                    th { background: #f5f5f5; font-weight: bold; }
                    .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }
                    .stat-card { border: 1px solid #ddd; padding: 12px; text-align: center; border-radius: 4px; }
                    .stat-value { font-size: 24px; font-weight: bold; color: #1976d2; }
                    .stat-label { font-size: 11px; color: #666; }
                    .print-date { text-align: right; font-size: 10px; color: #999; margin-top: 20px; }
                  </style>
                  </head><body>
                  <h1>University of Vavuniya - Leave Analytics Report</h1>
                  <p style="text-align: center; color: #666;">Period: ${
                    [
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ][analyticsMonth - 1]
                  } ${analyticsYear}</p>
                  ${el.innerHTML}
                  <div class="print-date">Printed on: ${new Date().toLocaleString()}</div>
                  </body></html>
                `);
                printWindow.document.close();
                printWindow.focus();
                setTimeout(() => {
                  printWindow.print();
                }, 300);
              }}
              disabled={!analyticsData}
            >
              Print Analytics
            </Button>
          </Box>

          {analyticsLoading ? (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <CircularProgress />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Loading analytics...
              </Typography>
            </Box>
          ) : analyticsData ? (
            <Box id="vc-analytics-print-area">
              {/* Year-to-Date Summary Cards */}
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center" }}
              >
                <TrendingUp sx={{ mr: 1 }} color="primary" />
                Year-to-Date Summary ({analyticsYear})
              </Typography>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined" sx={{ textAlign: "center", p: 2 }}>
                    <Typography
                      variant="h4"
                      color="primary.main"
                      fontWeight="bold"
                    >
                      {analyticsData.ytdSummary?.total_applications || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Total Applications
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined" sx={{ textAlign: "center", p: 2 }}>
                    <Typography
                      variant="h4"
                      color="success.main"
                      fontWeight="bold"
                    >
                      {analyticsData.ytdSummary?.total_approved || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Approved
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined" sx={{ textAlign: "center", p: 2 }}>
                    <Typography
                      variant="h4"
                      color="error.main"
                      fontWeight="bold"
                    >
                      {analyticsData.ytdSummary?.total_rejected || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Rejected
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined" sx={{ textAlign: "center", p: 2 }}>
                    <Typography
                      variant="h4"
                      color="info.main"
                      fontWeight="bold"
                    >
                      {analyticsData.ytdSummary?.total_days_taken || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Total Days Taken
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              {/* Department-wise Detailed Analysis */}
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center" }}
              >
                <BarChart sx={{ mr: 1 }} color="primary" />
                Department-wise Analysis (
                {
                  [
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                  ][analyticsMonth - 1]
                }{" "}
                {analyticsYear})
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ mb: 4 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Department
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Total Staff
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Approved
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Pending
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Rejected
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Total Days
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                        Utilisation
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(analyticsData.departmentAnalysis || []).map(
                      (dept: any) => {
                        const entitlement = dept.total_entitlement || 0;
                        const utilisation =
                          entitlement > 0
                            ? Math.round(
                                (dept.total_days_taken / entitlement) * 100,
                              )
                            : 0;
                        return (
                          <TableRow key={dept.department_id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {dept.department_name}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {dept.total_employees}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={dept.approved_count}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={dept.pending_count}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={dept.rejected_count}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontWeight: "bold" }}
                            >
                              {dept.total_days_taken}
                            </TableCell>
                            <TableCell align="center">
                              {entitlement > 0 ? entitlement : "—"}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min(utilisation, 100)}
                                    sx={{
                                      height: 10,
                                      borderRadius: 5,
                                      bgcolor: "#e0e0e0",
                                    }}
                                    color={
                                      utilisation > 80
                                        ? "error"
                                        : utilisation > 50
                                          ? "warning"
                                          : "primary"
                                    }
                                  />
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{ minWidth: 35 }}
                                  color={
                                    utilisation > 80
                                      ? "error.main"
                                      : utilisation > 50
                                        ? "warning.main"
                                        : "text.secondary"
                                  }
                                >
                                  {entitlement > 0 ? `${utilisation}%` : "N/A"}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      },
                    )}
                    {(analyticsData.departmentAnalysis || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No department data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Leave Type Distribution */}
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Analytics sx={{ mr: 1 }} color="primary" />
                Leave Type Distribution
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ mb: 4 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Leave Type
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Applications
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Approved
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Pending
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Rejected
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Total Days
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                        Proportion
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(analyticsData.leaveTypeDistribution || []).map(
                      (lt: any) => {
                        const totalAllDays = (
                          analyticsData.leaveTypeDistribution || []
                        ).reduce(
                          (sum: number, l: any) => sum + (l.total_days || 0),
                          0,
                        );
                        const colors = [
                          "#1976d2",
                          "#2e7d32",
                          "#ed6c02",
                          "#d32f2f",
                          "#9c27b0",
                          "#0288d1",
                          "#558b2f",
                        ];
                        const idx = (
                          analyticsData.leaveTypeDistribution || []
                        ).indexOf(lt);
                        const barColor = colors[idx % colors.length];
                        return (
                          <TableRow key={lt.leave_type_id} hover>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    bgcolor: barColor,
                                  }}
                                />
                                <Typography variant="body2" fontWeight="medium">
                                  {lt.leave_type}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              {lt.application_count}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={lt.approved}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={lt.pending}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={lt.rejected}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontWeight: "bold" }}
                            >
                              {lt.total_days}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={
                                      totalAllDays > 0
                                        ? Math.min(
                                            (lt.total_days / totalAllDays) *
                                              100,
                                            100,
                                          )
                                        : 0
                                    }
                                    sx={{
                                      height: 10,
                                      borderRadius: 5,
                                      bgcolor: "#e0e0e0",
                                      "& .MuiLinearProgress-bar": {
                                        backgroundColor: barColor,
                                      },
                                    }}
                                  />
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{ minWidth: 35 }}
                                >
                                  {totalAllDays > 0
                                    ? Math.round(
                                        (lt.total_days / totalAllDays) * 100,
                                      )
                                    : 0}
                                  %
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      },
                    )}
                    {(analyticsData.leaveTypeDistribution || []).length ===
                      0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No leave type data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Monthly Trend */}
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center" }}
              >
                <TrendingUp sx={{ mr: 1 }} color="primary" />
                Monthly Trend (Last 12 Months)
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ mb: 4 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>Month</TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Total Applications
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Approved
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Rejected
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Total Days
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>
                        Trend
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(analyticsData.monthlyTrend || []).map((mt: any) => {
                      const monthNames = [
                        "Jan",
                        "Feb",
                        "Mar",
                        "Apr",
                        "May",
                        "Jun",
                        "Jul",
                        "Aug",
                        "Sep",
                        "Oct",
                        "Nov",
                        "Dec",
                      ];
                      const maxApps = Math.max(
                        ...(analyticsData.monthlyTrend || []).map(
                          (m: any) => m.total_applications || 0,
                        ),
                        1,
                      );
                      return (
                        <TableRow key={`${mt.year}-${mt.month}`} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {monthNames[mt.month - 1]} {mt.year}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {mt.total_applications}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={mt.approved}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={mt.rejected}
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            {mt.total_days}
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(
                                    (mt.total_applications / maxApps) * 100,
                                    100,
                                  )}
                                  sx={{
                                    height: 10,
                                    borderRadius: 5,
                                    bgcolor: "#e0e0e0",
                                  }}
                                  color="info"
                                />
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{ minWidth: 20 }}
                              >
                                {mt.total_applications}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(analyticsData.monthlyTrend || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No trend data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Department Leave Type Breakdown */}
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center" }}
              >
                <BarChart sx={{ mr: 1 }} color="primary" />
                Department-wise Leave Type Breakdown
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ mb: 4 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Department
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Leave Type
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Applications
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Total Days
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(analyticsData.departmentLeaveTypes || []).map(
                      (dlt: any, idx: number) => (
                        <TableRow key={idx} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {dlt.department_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {dlt.leave_type}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{dlt.count}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            {dlt.total_days}
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                    {(analyticsData.departmentLeaveTypes || []).length ===
                      0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Top Leave Takers */}
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center" }}
              >
                <People sx={{ mr: 1 }} color="primary" />
                Top Leave Takers (This Month)
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ mb: 2 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Employee
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Department
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Designation
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Leave Count
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Total Days
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(analyticsData.topLeaveTakers || []).map(
                      (emp: any, idx: number) => (
                        <TableRow key={idx} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {emp.employee_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {emp.employee_code}
                            </Typography>
                          </TableCell>
                          <TableCell>{emp.department_name}</TableCell>
                          <TableCell>{emp.designation}</TableCell>
                          <TableCell align="center">
                            {emp.leave_count}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            {emp.total_days}
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                    {(analyticsData.topLeaveTakers || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No data available for this period
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Assessment sx={{ fontSize: 60, color: "#bdbdbd" }} />
              <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
                Select a period and click Generate to view analytics
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* University Calendar Dialog */}
      <Dialog
        open={teamCalendarOpen}
        onClose={() => setTeamCalendarOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">University Leave Calendar</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={handlePrevMonth} size="small">
                <NavigateBefore />
              </IconButton>
              <Typography>
                {new Date(calendarYear, calendarMonth).toLocaleDateString(
                  "en-US",
                  {
                    month: "long",
                    year: "numeric",
                  },
                )}
              </Typography>
              <IconButton onClick={handleNextMonth} size="small">
                <NavigateNext />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={0}>
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <Grid item xs={12 / 7} key={day}>
                  <Box
                    sx={{
                      p: 1,
                      textAlign: "center",
                      fontWeight: "bold",
                      bgcolor: "#f5f5f5",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    {day}
                  </Box>
                </Grid>
              ))}
              {renderCalendar().map((day, index) => (
                <Grid item xs={12 / 7} key={index}>
                  {day}
                </Grid>
              ))}
            </Grid>
          </Box>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: "#e3f2fd",
                  mr: 1,
                  border: "1px solid #90caf9",
                }}
              />
              <Typography variant="caption">Today</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: "#f5f5f5",
                  mr: 1,
                  border: "1px solid #e0e0e0",
                }}
              />
              <Typography variant="caption">Weekend</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamCalendarOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Calendar Day Popover */}
      <Popover
        open={Boolean(calendarPopover.anchorEl)}
        anchorEl={calendarPopover.anchorEl}
        onClose={() =>
          setCalendarPopover({ anchorEl: null, date: "", leaves: [] })
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, minWidth: 300, maxWidth: 400 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
            {calendarPopover.date &&
              new Date(calendarPopover.date + "T00:00:00").toLocaleDateString(
                "en-US",
                {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                },
              )}
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          {calendarPopover.leaves.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No staff on leave
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 350, overflow: "auto" }}>
              {/* Approved Leaves */}
              {calendarPopover.leaves.filter(
                (l: any) => l.status === "approved",
              ).length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ textTransform: "uppercase" }}
                  >
                    Approved Leaves (
                    {
                      calendarPopover.leaves.filter(
                        (l: any) => l.status === "approved",
                      ).length
                    }
                    )
                  </Typography>
                  {calendarPopover.leaves
                    .filter((l: any) => l.status === "approved")
                    .map((leave: any, idx: number) => (
                      <Box
                        key={idx}
                        sx={{
                          mt: 1,
                          p: 1.5,
                          bgcolor: "#f6ffed",
                          borderRadius: 1,
                          borderLeft: "3px solid #52c41a",
                          cursor: "pointer",
                          "&:hover": { bgcolor: "#d9f7be" },
                        }}
                        onClick={() => {
                          setCalendarPopover({
                            anchorEl: null,
                            date: "",
                            leaves: [],
                          });
                          window.location.href = `/leaves/${leave.leave_id}`;
                        }}
                      >
                        <Typography variant="body2" fontWeight="medium">
                          {leave.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {leave.department}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {leave.leave_type}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              )}

              {/* Pending Leaves */}
              {calendarPopover.leaves.filter((l: any) => l.status === "pending")
                .length > 0 && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ textTransform: "uppercase" }}
                  >
                    Pending Leaves (
                    {
                      calendarPopover.leaves.filter(
                        (l: any) => l.status === "pending",
                      ).length
                    }
                    )
                  </Typography>
                  {calendarPopover.leaves
                    .filter((l: any) => l.status === "pending")
                    .map((leave: any, idx: number) => (
                      <Box
                        key={idx}
                        sx={{
                          mt: 1,
                          p: 1.5,
                          bgcolor: "#fffbe6",
                          borderRadius: 1,
                          borderLeft: "3px solid #faad14",
                          cursor: "pointer",
                          "&:hover": { bgcolor: "#fff1b8" },
                        }}
                        onClick={() => {
                          setCalendarPopover({
                            anchorEl: null,
                            date: "",
                            leaves: [],
                          });
                          window.location.href = `/leaves/${leave.leave_id}`;
                        }}
                      >
                        <Typography variant="body2" fontWeight="medium">
                          {leave.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {leave.department}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {leave.leave_type}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Popover>

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedApplication && (
            <>
              Process Leave Application -{" "}
              {selectedApplication.application_number}
              <Typography variant="subtitle2" color="textSecondary">
                {selectedApplication.employee_name} (
                {selectedApplication.employee_code})
              </Typography>
            </>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Leave Type:</strong> {selectedApplication.leave_type}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Department:</strong>{" "}
                {selectedApplication.department_name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Duration:</strong>{" "}
                {new Date(selectedApplication.start_date).toLocaleDateString()}{" "}
                to {new Date(selectedApplication.end_date).toLocaleDateString()}{" "}
                ({selectedApplication.number_of_days} day
                {selectedApplication.number_of_days !== 1 ? "s" : ""})
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Reason:</strong> {selectedApplication.reason}
              </Typography>
            </Box>
          )}

          {approvalHistory.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Approval History:
              </Typography>
              <Box sx={{ pl: 1 }}>
                {approvalHistory.map((history: any, index: number) => {
                  let actionText = "";
                  if (history.action === "substitute_approve") {
                    actionText = `accepted by substitute ${history.approver_name}`;
                  } else if (history.action === "substitute_reject") {
                    actionText = `rejected by substitute ${history.approver_name}`;
                  } else if (
                    history.action === "forward" ||
                    history.action === "recommend"
                  ) {
                    actionText = `${history.action}ed by ${history.approver_name} (${history.approver_role})${
                      history.next_approver_name
                        ? ` to ${history.next_approver_name} (${history.next_approver_role})`
                        : ""
                    }`;
                  } else {
                    actionText = `${history.action}${
                      history.action.endsWith("e") ? "d" : "ed"
                    } by ${history.approver_name} (${history.approver_role})`;
                  }

                  return (
                    <Box key={history.id || index} sx={{ mb: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>
                          •{" "}
                          {actionText.charAt(0).toUpperCase() +
                            actionText.slice(1)}
                        </strong>
                        {history.comments && (
                          <span> - "{history.comments}"</span>
                        )}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(history.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comments (Optional)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Add any comments..."
            disabled={loading}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1, p: 2 }}>
          <Button
            onClick={() => setApprovalDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => processApproval("return")}
            color="warning"
            variant="outlined"
            disabled={loading}
            size="small"
          >
            Return
          </Button>
          <Button
            onClick={() => processApproval("rejected")}
            color="error"
            variant="outlined"
            disabled={loading}
            startIcon={<ThumbDown />}
          >
            Reject
          </Button>
          {selectedApplication?.action_type === "recommend" ? (
            <>
              <Button
                onClick={() => processApproval("recommend")}
                color="primary"
                variant="outlined"
                disabled={loading}
                startIcon={<ThumbUp />}
              >
                Recommend
              </Button>
              <Button
                onClick={() => processApproval("forward")}
                color="info"
                variant="outlined"
                disabled={loading}
              >
                Forward
              </Button>
            </>
          ) : (
            <Button
              onClick={() => processApproval("approved")}
              color="success"
              variant="contained"
              disabled={loading}
              startIcon={<ThumbUp />}
            >
              Approve
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VCDashboard;
